/**
 * Router — 路由配置（Rivmoon Console，自研设计系统，无 AntD）
 *
 * 路由树：
 *   /auth/*                  AuthLayout（鉴权区，6 页）
 *     login / verify-2fa / bind-totp / accept-invite
 *     password-reset/request / password-reset/confirm
 *
 *   RequireAuth (mfa_verified) → <AppShell/>（侧栏 + 顶栏壳，react-router <Outlet/>）
 *     /                       DashboardPage
 *     /metrics                → /metrics/activation-conversion（数据中台无总览页，落到侧栏本组首项）
 *     /metrics/engagement     MetricsEngagementPage（内容消费 L2-B · 留存曲线 + 阅读深度趋势 + 内容类型对比）
 *     /metrics/monetization   MetricsMonetizationPage（商业化 L2-C · 四卡 + ARPPU 趋势 + 六级付费漏斗）
 *     /metrics/activation-conversion MetricsActivationConversionPage（激活转化看板 · 分组表格 / 时间趋势 / 转化链路）
 *     /metrics/web-funnels    MetricsWebFunnelsPage（Web 行为漏斗 · 单个 WEB App 七个有序分析 + 到账核对）
 *     /metrics/first-payment-cohorts MetricsFirstPaymentCohortsPage（付费 cohort · 按语言的首付天数分布）
 *     /metrics/retention-cohorts MetricsRetentionCohortsPage（用户留存 · 激活 / 首付 cohort × 天周月回访）
 *     /metrics/paywall-conversion MetricsPaywallConversionPage（付费转化看板 · 分组表格 / 时间趋势 / 转化链路）
 *     /metrics/ltv            MetricsLtvPage（LTV 队列 L2-E · cohort 热力表 + 累积曲线）
 *     /metrics/diagnosis      MetricsDiagnosisPage（诊断下钻 L3 · 维度组合查询器 + 明细表）
 *     /apps                   AppsPage
 *     /books                  BooksPage
 *     /paywall                PaywallPage
 *     /finance                FinancePage
 *     /users                  UsersPage
 *     /feedback               FeedbackPage
 *     /push                   PushPage
 *     /ops                    OpsPage
 *     /landing                → /landing/projects（项目管理）
 *     /landing/settings       LandingPage（投放设置：产品 / 模板 / 默认文案）
 *     /settings               SettingsLayout（9 个二级路由：members/roles/audit-logs/
 *                             regions/integrations/webhooks/notifications/advanced/design-system）
 *                             integrations/payment-providers/:channel = 框架通道配置与回调登记页
 *     /server-monitor         ServerMonitorPage（服务器监控，RequireRole root/admin）
 *     /system-config          SystemConfigPage
 *     /profile                ProfilePage
 *     /design-system          DesignSystemPage
 *     *                       → Navigate '/'
 */

import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { AppShell } from '~/app/AppShell';
import AppsPage from '~/pages/apps/AppsPage';
import AcceptInvitePage from '~/pages/auth/AcceptInvitePage';
import AuthLayout from '~/pages/auth/AuthLayout';
import BindTotpPage from '~/pages/auth/BindTotpPage';
import LoginPage from '~/pages/auth/LoginPage';
import PasswordResetConfirmPage from '~/pages/auth/PasswordResetConfirmPage';
import PasswordResetRequestPage from '~/pages/auth/PasswordResetRequestPage';
import Verify2FAPage from '~/pages/auth/Verify2FAPage';
import BooksPage from '~/pages/books/BooksPage';
import DashboardPage from '~/pages/dashboard/DashboardPage';
import DesignSystemPage from '~/pages/design-system/DesignSystemPage';
import FeedbackPage from '~/pages/feedback/FeedbackPage';
import FinancePage from '~/pages/finance/FinancePage';
import LandingPage from '~/pages/landing/LandingPage';
import MetricsActivationConversionPage from '~/pages/metrics/MetricsActivationConversionPage';
import MetricsDiagnosisPage from '~/pages/metrics/MetricsDiagnosisPage';
import MetricsEngagementPage from '~/pages/metrics/MetricsEngagementPage';
import MetricsFirstPaymentCohortsPage from '~/pages/metrics/MetricsFirstPaymentCohortsPage';
import MetricsLtvPage from '~/pages/metrics/MetricsLtvPage';
import MetricsMonetizationPage from '~/pages/metrics/MetricsMonetizationPage';
import MetricsPaywallConversionPage from '~/pages/metrics/MetricsPaywallConversionPage';
import MetricsRetentionCohortsPage from '~/pages/metrics/MetricsRetentionCohortsPage';
import MetricsRewardedAdsPage from '~/pages/metrics/MetricsRewardedAdsPage';
import MetricsUsdCoveragePage from '~/pages/metrics/MetricsUsdCoveragePage';
import MetricsWebFunnelsPage from '~/pages/metrics/MetricsWebFunnelsPage';
import OpsPage from '~/pages/ops/OpsPage';
import PaywallPage from '~/pages/paywall/PaywallPage';
import ProfilePage from '~/pages/profile/ProfilePage';
import PushPage from '~/pages/push/PushPage';
import ServerMonitorPage from '~/pages/server-monitor/ServerMonitorPage';
import AdvancedPage from '~/pages/settings/AdvancedPage';
import AuditLogPage from '~/pages/settings/AuditLogPage';
import IntegrationsPage from '~/pages/settings/IntegrationsPage';
import ModelConfigurationsPage from '~/pages/settings/ModelConfigurationsPage';
import NotificationsPage from '~/pages/settings/NotificationsPage';
import ProhibitedTermsPage from '~/pages/settings/ProhibitedTermsPage';
import RolesPage from '~/pages/settings/RolesPage';
import SettingsLayout from '~/pages/settings/SettingsLayout';
import TeamMembersPage from '~/pages/settings/TeamMembersPage';
import WebhooksPage from '~/pages/settings/WebhooksPage';
import PaymentProviderConfigPage from '~/pages/settings/payment-providers/PaymentProviderConfigPage';
import SystemConfigPage from '~/pages/system-config/SystemConfigPage';
import UsersPage from '~/pages/users/UsersPage';
import { RequireAuth, RequireRole } from './guards';

export const router = createBrowserRouter([
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { path: 'login', element: <LoginPage /> },
      { path: 'verify-2fa', element: <Verify2FAPage /> },
      { path: 'bind-totp', element: <BindTotpPage /> },
      { path: 'accept-invite', element: <AcceptInvitePage /> },
      { path: 'password-reset/request', element: <PasswordResetRequestPage /> },
      { path: 'password-reset/confirm', element: <PasswordResetConfirmPage /> },
    ],
  },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'metrics', element: <Navigate to="/metrics/activation-conversion" replace /> },
          { path: 'metrics/engagement', element: <MetricsEngagementPage /> },
          { path: 'metrics/monetization', element: <MetricsMonetizationPage /> },
          { path: 'metrics/activation-conversion', element: <MetricsActivationConversionPage /> },
          { path: 'metrics/web-funnels', element: <MetricsWebFunnelsPage /> },
          { path: 'metrics/first-payment-cohorts', element: <MetricsFirstPaymentCohortsPage /> },
          { path: 'metrics/retention-cohorts', element: <MetricsRetentionCohortsPage /> },
          { path: 'metrics/paywall-conversion', element: <MetricsPaywallConversionPage /> },
          { path: 'metrics/ltv', element: <MetricsLtvPage /> },
          { path: 'metrics/usd-coverage', element: <MetricsUsdCoveragePage /> },
          { path: 'metrics/rewarded-ads', element: <MetricsRewardedAdsPage /> },
          { path: 'metrics/diagnosis', element: <MetricsDiagnosisPage /> },
          { path: 'apps', element: <AppsPage /> },
          { path: 'books', element: <BooksPage /> },
          { path: 'paywall', element: <PaywallPage /> },
          { path: 'finance', element: <FinancePage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'feedback', element: <FeedbackPage /> },
          { path: 'push', element: <PushPage /> },
          { path: 'ops', element: <OpsPage /> },
          { path: 'landing', element: <Navigate to="/landing/projects" replace /> },
          { path: 'landing/projects', element: <LandingPage /> },
          { path: 'landing/settings', element: <LandingPage settings /> },
          { path: 'landing/projects/:projectId', element: <LandingPage /> },
          {
            // 系统设置：9 个二级菜单 = 9 个独立路由页，SettingsLayout 提供左侧菜单 + <Outlet/>
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="members" replace /> },
              { path: 'members', element: <TeamMembersPage /> },
              { path: 'roles', element: <RolesPage /> },
              { path: 'audit-logs', element: <AuditLogPage /> },
              { path: 'regions', element: <SystemConfigPage /> },
              { path: 'model-configurations', element: <ModelConfigurationsPage /> },
              { path: 'prohibited-terms', element: <ProhibitedTermsPage /> },
              { path: 'integrations', element: <IntegrationsPage /> },
              {
                // 框架通道配置与回调登记（ADM-PAY-S012）；`:channel` 只认已登记框架通道，页面内校验
                path: 'integrations/payment-providers/:channel',
                element: <PaymentProviderConfigPage />,
              },
              { path: 'webhooks', element: <WebhooksPage /> },
              { path: 'notifications', element: <NotificationsPage /> },
              { path: 'advanced', element: <AdvancedPage /> },
              { path: 'design-system', element: <DesignSystemPage embedded /> },
            ],
          },
          {
            // 服务器监控：前端守卫只是 UX，后端 /api/v1/server-monitor/* requireRole('root','admin') 才是权威
            element: <RequireRole roles={['root', 'admin']} />,
            children: [{ path: 'server-monitor', element: <ServerMonitorPage /> }],
          },
          { path: 'system-config', element: <SystemConfigPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'design-system', element: <DesignSystemPage /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
